import { useState } from 'react';
import { Menu } from '../../components/ui';
import { Icon } from '../../components/ui/Icon';
import { TASK_PRIORITY_LABELS } from '../tasks/types';
import type { ReactNode } from 'react';
import type { BadgeTone, MenuItem } from '../../components/ui';
import type { IconName } from '../../components/ui/Icon';
import type { ProjectMemberWithProfile, ProjectStatus } from '../projects/types';
import type { Task, TaskPriority, TaskUpdate } from '../tasks/types';

/**
 * The default List View columns (handoff 17). Stage 9 makes the cells
 * editable in place: completion, title, assignee, status, due date, priority
 * and remarks write straight back to the row, and a row menu reorders a task
 * within its section or moves it to the end of another one. Date assigned and
 * date completed stay read-only because the database stamps them.
 *
 * Nothing here decides permission. canEdit hides controls as a convenience;
 * Row Level Security refuses anything the caller may not do. Completion and
 * status stay fully independent: the checkbox never touches status_id and the
 * status menu never touches is_completed.
 */

export interface TaskLookups {
  members: ProjectMemberWithProfile[];
  statuses: ProjectStatus[];
}

/** A section a task can be moved to from the row menu. */
export interface MoveTarget {
  id: string;
  name: string;
}

/** What a row may do. The List View wires these to its mutations. */
/** Shared by the row grip and the section drop target. */
export const TASK_DRAG_MIME = 'application/x-tap-task';

export interface TaskRowActions {
  /** A row was dropped so it should land at `index` of the visible list. */
  onDropBefore?: (taskId: string, index: number) => void;
  onEdit: (task: Task, changes: TaskUpdate) => void;
  onToggleComplete: (task: Task, isCompleted: boolean) => void;
  onMove: (task: Task, direction: 'up' | 'down') => void;
  onMoveToSection: (task: Task, sectionId: string) => void;
  onOpenDetails: (task: Task) => void;
  /**
   * Ask for the delete confirmation. The row never deletes on its own — the
   * List View owns the dialog and the mutation, so the row menu and the
   * details panel cannot disagree about what a delete does.
   */
  onDelete: (task: Task) => void;
}

/**
 * The List View columns. `personal` marks the ones a private list keeps:
 * a personal space has no one to assign to and no manager to report to, so
 * Assignee, Date completed and Remarks are hidden there (redesign spec:
 * Personal Tasks is Todoist-light). Header and cells read the same model,
 * so a column can never disappear from one and not the other.
 */
const TASK_COLUMNS: { key: string; heading: string; personal: boolean }[] = [
  { key: 'done', heading: 'Done', personal: true },
  { key: 'task', heading: 'Task', personal: true },
  { key: 'assignee', heading: 'Assignee', personal: false },
  { key: 'status', heading: 'Status', personal: true },
  { key: 'due', heading: 'Due date', personal: true },
  { key: 'assigned', heading: 'Date assigned', personal: true },
  { key: 'completed', heading: 'Date completed', personal: false },
  { key: 'priority', heading: 'Priority', personal: true },
  { key: 'remarks', heading: 'Remarks', personal: false },
];

/** Columns hidden when the table renders a personal list. */
const PERSONAL_HIDDEN = new Set(
  TASK_COLUMNS.filter((column) => !column.personal).map((column) => column.key),
);

const PRIORITY_OPTIONS: { value: TaskPriority; label: string }[] = [
  { value: 'high', label: TASK_PRIORITY_LABELS.high },
  { value: 'medium', label: TASK_PRIORITY_LABELS.medium },
  { value: 'low', label: TASK_PRIORITY_LABELS.low },
];

export function memberLabel(member: ProjectMemberWithProfile): string {
  return member.display_name.trim() !== '' ? member.display_name : member.email;
}

/** A date the person can read. Timestamps are shown as their date only. */
export function formatDate(value: string | null): string {
  if (!value) {
    return '—';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '—';
  }
  return parsed.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function statusTone(colorKey: string): BadgeTone {
  const known: BadgeTone[] = [
    'neutral',
    'brand',
    'success',
    'warning',
    'danger',
    'review',
    'revision',
  ];
  return known.includes(colorKey as BadgeTone) ? (colorKey as BadgeTone) : 'neutral';
}

/**
 * Overdue is a display fact, not a stored one: a due date in the past on a
 * task that is not complete.
 */
export function isOverdue(task: Task, today: Date = new Date()): boolean {
  if (task.is_completed || !task.due_date) {
    return false;
  }
  const due = new Date(`${task.due_date}T00:00:00`);
  if (Number.isNaN(due.getTime())) {
    return false;
  }
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return due < startOfToday;
}

/**
 * The editor half of InlineText, on its own so a caller can decide what opens
 * it.
 *
 * The row title needs exactly this input but must NOT be a click-to-edit
 * button — clicking a task's name has to open the task. Extracted rather than
 * bolting a "controlled" mode onto InlineText, because the two callers want
 * genuinely different things: remarks and subtask titles are still
 * click-to-edit and keep the component below unchanged.
 */
export function InlineTextInput({
  value,
  inputLabel,
  allowEmpty,
  onSave,
  onClose,
}: {
  value: string;
  inputLabel: string;
  allowEmpty: boolean;
  onSave: (next: string) => void;
  /** Called after a commit or a cancel, so the caller can drop its edit state. */
  onClose: () => void;
}) {
  const [draft, setDraft] = useState(value);

  function commit() {
    onClose();
    const trimmed = draft.trim();
    if (trimmed === value || (trimmed === '' && !allowEmpty)) {
      return;
    }
    onSave(trimmed);
  }

  return (
    <input
      className="task-cell-input"
      aria-label={inputLabel}
      value={draft}
      autoFocus
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          commit();
        }
        if (event.key === 'Escape') {
          event.preventDefault();
          setDraft(value);
          onClose();
        }
      }}
    />
  );
}

/**
 * Click-to-edit text. The value is a button until clicked, then an input that
 * saves on Enter or blur and cancels on Escape. An empty result is a revert
 * unless allowEmpty says clearing is a real edit (remarks yes, title no).
 * Shared with the task details panel, which reuses it for subtask titles.
 *
 * NOT used for the row title any more. Clicking a task's name has to open the
 * task — see the title cell below.
 */
export function InlineText({
  value,
  buttonLabel,
  inputLabel,
  display,
  allowEmpty,
  onSave,
}: {
  value: string;
  buttonLabel: string;
  inputLabel: string;
  display: ReactNode;
  allowEmpty: boolean;
  onSave: (next: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  function open() {
    setDraft(value);
    setEditing(true);
  }

  function commit() {
    setEditing(false);
    const trimmed = draft.trim();
    if (trimmed === value || (trimmed === '' && !allowEmpty)) {
      return;
    }
    onSave(trimmed);
  }

  if (!editing) {
    return (
      <button type="button" className="task-title-button" aria-label={buttonLabel} onClick={open}>
        {display}
      </button>
    );
  }

  return (
    <input
      className="task-cell-input"
      aria-label={inputLabel}
      value={draft}
      autoFocus
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          commit();
        }
        if (event.key === 'Escape') {
          event.preventDefault();
          setDraft(value);
          setEditing(false);
        }
      }}
    />
  );
}

/**
 * One count in the inline micro-meta strip beside a task title.
 *
 * Returns null at zero, so an empty row carries no strip at all and the title
 * gets the full cell.
 */
function metaFlag(
  count: number,
  glyph: IconName | 'subtasks',
  singular: string,
  plural: string,
): ReactNode {
  if (count <= 0) return null;
  const label = `${String(count)} ${count === 1 ? singular : plural}`;
  return (
    <span className="task-meta" title={label}>
      <span aria-hidden="true">
        {glyph === 'subtasks' ? '☑' : <Icon name={glyph} size="sm" />} {count}
      </span>
      <span className="task-meta-label">{label}</span>
    </span>
  );
}

function TaskRow({
  task,
  rowIndex,
  lookups,
  subtaskCount,
  commentCount,
  fileCount,
  canEdit,
  moveTargets,
  actions,
  compact = false,
}: {
  task: Task;
  /** Position in the visible list, which is what a drop index means. */
  rowIndex: number;
  lookups: TaskLookups;
  subtaskCount: number;
  commentCount: number;
  fileCount: number;
  canEdit: boolean;
  moveTargets: MoveTarget[];
  actions: TaskRowActions;
  compact?: boolean;
}) {
  const shows = (key: string) => !(compact && PERSONAL_HIDDEN.has(key));
  const assignee = lookups.members.find((member) => member.user_id === task.assignee_id);
  const status = lookups.statuses.find((row) => row.id === task.status_id);
  const overdue = isOverdue(task);
  // Renaming is opened from the row menu below, so the row owns the state
  // rather than the title control owning it. Declared here rather than with
  // the other row state further down, because the menu reads it first.
  const [renamingTitle, setRenamingTitle] = useState(false);

  // Inactive statuses are not offered, but a row already carrying one keeps
  // its current choice visible rather than silently losing it.
  const statusOptions = lookups.statuses.filter(
    (row) => row.is_active || row.id === task.status_id,
  );
  const [editingStatus, setEditingStatus] = useState(false);
  const statusPillClass = `hub-status status-pill task-status-button${
    status === undefined ? '' : ` hub-status--${statusTone(status.color_key)}`
  }`;

  const menuItems: MenuItem[] = [
    // First, because it is the one that used to happen by accident. The title
    // opens the task now; renaming is asked for rather than stumbled into.
    { label: 'Rename', onSelect: () => setRenamingTitle(true) },
    { label: 'Move up', onSelect: () => actions.onMove(task, 'up') },
    { label: 'Move down', onSelect: () => actions.onMove(task, 'down') },
    ...moveTargets.map((target) => ({
      label: `Move to ${target.name}`,
      onSelect: () => actions.onMoveToSection(task, target.id),
    })),
    // Last, and destructive. Same wording as the details panel so the two
    // entry points read as one action rather than two similar ones.
    { label: 'Delete task…', danger: true, onSelect: () => actions.onDelete(task) },
  ];

  const overdueLabel = overdue ? <span className="ui-visually-hidden">Overdue</span> : null;
  // Subtask, comment and attachment counts used to be three stacked blocks
  // under the title, so a row with all three ran four lines tall. They are one
  // inline strip now, to the right of the title on the same line.
  //
  // The glyph is aria-hidden and the full wording rides alongside it in
  // visually-hidden text, so a screen reader still hears "3 comments" rather
  // than the character name of an emoji. Same pattern the theme switcher uses.
  const subtaskFlag = metaFlag(subtaskCount, 'subtasks', 'subtask', 'subtasks');
  // The has-comments indicator (handoff 17): count only, loaded in one
  // project-wide read; the thread itself lives in the details panel.
  const commentFlag = metaFlag(commentCount, 'comment', 'comment', 'comments');
  // The has-attachments indicator (handoff 17), same one-read pattern.
  const fileFlag = metaFlag(fileCount, 'files', 'file', 'files');

  const [completing, setCompleting] = useState(false);
  const [dropBefore, setDropBefore] = useState(false);

  return (
    <tr
      className={`task-row${task.is_completed ? ' task-row--complete' : ''}${
        completing ? ' task-row--completing' : ''
      }${dropBefore ? ' task-row--drop-before' : ''}`}
      onDragOver={
        actions.onDropBefore === undefined
          ? undefined
          : (event) => {
              if (!event.dataTransfer.types.includes(TASK_DRAG_MIME)) return;
              event.preventDefault();
              event.dataTransfer.dropEffect = 'move';
              setDropBefore(true);
            }
      }
      onDragLeave={() => setDropBefore(false)}
      onDrop={
        actions.onDropBefore === undefined
          ? undefined
          : (event) => {
              if (!event.dataTransfer.types.includes(TASK_DRAG_MIME)) return;
              event.preventDefault();
              // Stop the section body from also handling it and appending the
              // row to the end, which would beat the precise drop.
              event.stopPropagation();
              setDropBefore(false);
              const taskId = event.dataTransfer.getData(TASK_DRAG_MIME);
              if (taskId !== '') actions.onDropBefore?.(taskId, rowIndex);
            }
      }
    >
      <td className="task-cell task-cell--grip">
        {canEdit ? (
          <span
            className="task-grip"
            draggable
            role="button"
            tabIndex={-1}
            aria-label={`Move ${task.title}`}
            title="Drag to another section"
            onDragStart={(event) => {
              event.dataTransfer.setData(TASK_DRAG_MIME, task.id);
              event.dataTransfer.effectAllowed = 'move';
            }}
          >
            {/* Only the grip is draggable, not the row: a draggable row would
                swallow text selection and make the inline cells unusable. */}
            ⋮⋮
          </span>
        ) : null}
      </td>
      <td className="task-cell task-cell--done">
        <input
          type="checkbox"
          className="task-check"
          checked={task.is_completed}
          disabled={!canEdit}
          aria-label={task.is_completed ? `Reopen ${task.title}` : `Complete ${task.title}`}
          onChange={() => {
            if (!task.is_completed) {
              setCompleting(true);
              window.setTimeout(() => {
                actions.onToggleComplete(task, true);
                setCompleting(false);
              }, 200);
            } else {
              actions.onToggleComplete(task, false);
            }
          }}
        />
      </td>
      <th scope="row" className="task-cell task-cell--title">
        {/* One line: the title takes the space that is left and truncates, the
            meta strip keeps its natural width on the right. The full title is
            in the row's own title attribute and in the drawer. */}
        {/* The title opens the task. It used to rename it.
         *
         * That was true before this pass as well, but it was survivable then
         * because a button plainly labelled "Details" sat next to it on every
         * row. Drop 5 traded that button for a faint ↗ pinned to the far side
         * of the cell, visible only on hover — so the obvious thing to click
         * became the title, and the title renamed. Tidier row, unusable page.
         *
         * Renaming is a deliberate act now and lives in the row menu, which is
         * where every other per-task action already is. Nothing has to be
         * hovered to be discovered, and a mis-click costs you a drawer you can
         * close rather than an edit you have to undo. */}
        <span className="task-title-line">
          {renamingTitle ? (
            <InlineTextInput
              value={task.title}
              inputLabel={`Title of ${task.title}`}
              allowEmpty={false}
              onSave={(next) => actions.onEdit(task, { title: next })}
              onClose={() => setRenamingTitle(false)}
            />
          ) : (
            <button
              type="button"
              className="task-title-button"
              /* Same accessible name the ↗ carried, because it is the same
                 job. Anything aimed at "Open details for X" still lands. */
              aria-label={`Open details for ${task.title}`}
              onClick={() => actions.onOpenDetails(task)}
            >
              <span className="task-title" title={task.title}>
                {task.title}
              </span>
            </button>
          )}
          {subtaskFlag}
          {commentFlag}
          {fileFlag}
        </span>
      </th>
      {shows('assignee') && (
        <td className="task-cell">
          {canEdit ? (
            <select
              className="task-cell-input"
              aria-label={`Assignee for ${task.title}`}
              value={task.assignee_id ?? ''}
              onChange={(event) =>
                actions.onEdit(task, {
                  assignee_id: event.target.value === '' ? null : event.target.value,
                })
              }
            >
              <option value="">Unassigned</option>
              {lookups.members.map((member) => (
                <option key={member.user_id} value={member.user_id}>
                  {memberLabel(member)}
                </option>
              ))}
            </select>
          ) : assignee ? (
            memberLabel(assignee)
          ) : (
            'Unassigned'
          )}
        </td>
      )}
      <td className="task-cell">
        {canEdit && editingStatus ? (
          <select
            className="task-cell-input"
            aria-label={`Status for ${task.title}`}
            autoFocus
            value={task.status_id ?? ''}
            onBlur={() => setEditingStatus(false)}
            onChange={(event) => {
              actions.onEdit(task, {
                status_id: event.target.value === '' ? null : event.target.value,
              });
              setEditingStatus(false);
            }}
          >
            <option value="">No status</option>
            {statusOptions.map((row) => (
              <option key={row.id} value={row.id}>
                {row.name}
              </option>
            ))}
          </select>
        ) : canEdit ? (
          <button
            type="button"
            className={statusPillClass}
            aria-label={`Edit status for ${task.title}`}
            onClick={() => setEditingStatus(true)}
          >
            {status?.name ?? 'No status'}
          </button>
        ) : (
          <span className={statusPillClass}>{status?.name ?? 'No status'}</span>
        )}
      </td>
      <td className="task-cell">
        {canEdit ? (
          /* The wrapper is what lets an unset date read as an em dash: an
             <input> is a replaced element, so the dash has to be drawn by
             something around it. */
          <span
            className={`task-date${task.due_date === null ? ' task-date--empty' : ''}${
              overdue ? ' task-date--overdue' : ''
            }`}
          >
            <input
              type="date"
              className="task-cell-input task-cell-input--date"
              aria-label={`Due date for ${task.title}`}
              value={task.due_date ?? ''}
              onChange={(event) =>
                actions.onEdit(task, {
                  due_date: event.target.value === '' ? null : event.target.value,
                })
              }
            />
          </span>
        ) : (
          <span className={overdue ? 'task-due task-due--overdue' : 'task-due'}>
            {formatDate(task.due_date)}
          </span>
        )}
        {overdueLabel}
      </td>
      <td className="task-cell">{formatDate(task.assigned_at)}</td>
      {shows('completed') && <td className="task-cell">{formatDate(task.completed_at)}</td>}
      <td className="task-cell">
        {canEdit ? (
          <select
            className="task-cell-input"
            aria-label={`Priority for ${task.title}`}
            value={task.priority ?? ''}
            onChange={(event) =>
              actions.onEdit(task, {
                priority: event.target.value === '' ? null : (event.target.value as TaskPriority),
              })
            }
          >
            <option value="">None</option>
            {PRIORITY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        ) : task.priority ? (
          TASK_PRIORITY_LABELS[task.priority]
        ) : (
          '—'
        )}
      </td>
      {shows('remarks') && (
        <td className="task-cell task-cell--remarks">
          {canEdit ? (
            <InlineText
              value={task.remarks ?? ''}
              buttonLabel={`Edit remarks of ${task.title}`}
              inputLabel={`Remarks of ${task.title}`}
              display={<span className="cell-clamp">{task.remarks ?? '—'}</span>}
              allowEmpty={true}
              onSave={(next) => actions.onEdit(task, { remarks: next === '' ? null : next })}
            />
          ) : (
            <span className="cell-clamp">{task.remarks ?? '—'}</span>
          )}
        </td>
      )}
      {canEdit ? (
        <td className="task-cell task-cell--actions">
          <Menu
            trigger={<span aria-hidden="true">•••</span>}
            triggerLabel={`Task actions for ${task.title}`}
            items={menuItems}
          />
        </td>
      ) : null}
    </tr>
  );
}

export function TaskTable({
  tasks,
  lookups,
  subtaskCounts,
  commentCounts,
  fileCounts,
  caption,
  canEdit,
  moveTargets,
  actions,
  compact = false,
}: {
  tasks: Task[];
  lookups: TaskLookups;
  subtaskCounts: Record<string, number>;
  commentCounts: Record<string, number>;
  fileCounts: Record<string, number>;
  caption: string;
  canEdit: boolean;
  moveTargets: MoveTarget[];
  actions: TaskRowActions;
  compact?: boolean;
}) {
  const visible = TASK_COLUMNS.filter(
    (column) => !(compact && PERSONAL_HIDDEN.has(column.key)),
  ).map((column) => column.heading);
  const headings = canEdit ? [...visible, 'Actions'] : visible;
  // The grip column has no label but still needs a cell, or every heading
  // after it sits above the wrong column.
  const leadingCols = 1;

  return (
    <div className="task-table-frame">
      <div className="task-table-scroll">
        <table className="task-table">
          <caption className="task-table-caption">{caption}</caption>
          <thead>
            <tr>
              {Array.from({ length: leadingCols }, (_, index) => (
                <th key={`grip-${String(index)}`} scope="col" className="task-heading">
                  <span className="ui-field-label--hidden">Reorder</span>
                </th>
              ))}
              {headings.map((heading) => (
                <th key={heading} scope="col" className="task-heading">
                  {heading}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tasks.map((task, index) => (
              <TaskRow
                key={task.id}
                task={task}
                rowIndex={index}
                lookups={lookups}
                subtaskCount={subtaskCounts[task.id] ?? 0}
                commentCount={commentCounts[task.id] ?? 0}
                fileCount={fileCounts[task.id] ?? 0}
                canEdit={canEdit}
                moveTargets={moveTargets}
                actions={actions}
                compact={compact}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
